<script lang="ts" context="module">
  /**
   * Props Convenience Type Export
   * ---------------------------------------------------------------------------
   */

  import type { ComponentProps } from "svelte";
  import ButtonBlock from "./ButtonBlock.svelte"; // self reference
  import type { ButtonProps } from "@elements/Button.svelte";
  export type ButtonBlockProps = ComponentProps<ButtonBlock>;

  const selectIconName = (
    icon?: StatamicIconProps | null,
  ): IconName | undefined =>
    (icon?.name ||
      (typeof icon?.icon === "string" ? icon.icon : icon?.icon?.value)) as
      | IconName
      | undefined;

  export const convertToButtonProps = (
    button: ButtonBlockProps,
  ): ButtonProps => {
    let output: ButtonProps = {
      text: button.text,
      openInModal: button.open_in_modal,
      openInNewTab: button.open_in_new_tab,
      shareData: button.shareData,
      variant:
        typeof button?.variant === "object"
          ? button.variant.value
          : button?.variant,
      size: typeof button?.size === "object" ? button.size.value : button?.size,
      leftIcon: convertToIconProps(button?.left_icon),
      rightIcon: convertToIconProps(button?.right_icon),
      color: button?.color,
      onClick: button?.onClick,
    };

    if (button.href && typeof button.href === "string") {
      output.href = button.href;
    }

    return output;
  };

  export const convertToIconProps = (
    icon: StatamicIconProps | null | undefined,
  ): IconProps | null => {
    return {
      name: selectIconName(icon),
      size: icon?.width,
      focusable: icon?.focusable,
    };
  };
</script>

<script lang="ts">
  /**
   * Imports
   * ---------------------------------------------------------------------------
   */

  import type {
    ButtonVariant,
    ButtonSize,
    ButtonColor,
  } from "@elements/Button.svelte";
  import Button from "@elements/Button.svelte";
  import type { IconName, IconProps } from "@elements/Icon.svelte";
  import type { StatamicLink, StatamicIconProps } from "@customTypes/statamic";

  /**
   * Props
   * ---------------------------------------------------------------------------
   */

  export let text: string;
  export let href: StatamicLink | string | null = null;
  export let open_in_modal = false;
  export let open_in_new_tab: boolean = false;
  export let shareData: ShareData | null = null;
  export let variant: ButtonVariant | { value: ButtonVariant } = "primary";
  export let size: ButtonSize | { value: ButtonSize } = "large";
  export let left_icon: StatamicIconProps | null = null;
  export let right_icon: StatamicIconProps | null = null;
  export let color: ButtonColor = "black";
  export let onClick: VoidFunction | null = null;
  export const type: "button" = "button";

  /**
   * Data
   * ---------------------------------------------------------------------------
   */

  const selectedHref = typeof href === "string" ? href : href?.url || "";
  const selectedVariant = typeof variant === "object" ? variant.value : variant;
</script>

<!-- Template ----------------------------------------------------------------->

<Button
  class={$$props.class}
  {text}
  href={selectedHref}
  openInModal={open_in_modal}
  openInNewTab={open_in_new_tab}
  {shareData}
  variant={selectedVariant}
  size={typeof size === "object" ? size.value : size}
  leftIcon={{
    name: selectIconName(left_icon),
    size: left_icon?.width,
    focusable: left_icon?.focusable,
  }}
  rightIcon={{
    name: selectIconName(right_icon),
    size: right_icon?.width,
    focusable: right_icon?.focusable,
  }}
  {color}
  {onClick}
/>
